// src/components/ReturnsViewerV2.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  limit,
  where,
  orderBy,
  query,
  startAfter,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useNavigate } from "react-router-dom";

import { buildReturnsV2PdfSpec, pdfDownloadFromSpec } from "../pdf/returnsV2PdfSpecs";
import { getThumbsForReturnsV2 } from "../pdf/spImages";

const RETURNS_COL = "returns_v2";
const PAGE_SIZE = 30;

const STATUS_OPTIONS = [
  { value: "new", label: "New" },                 // renamed (optional)
  { value: "waiting_collection", label: "Awaiting collection" }, // renamed (optional)
  { value: "waiting_processing", label: "Waiting to be processed" },
  { value: "in_progress", label: "In progress" },
  { value: "straggler", label: "Straggler" },

  // ✅ new statuses
  { value: "s039_warranty", label: "S039 - Warranty status" },
  { value: "office_processing", label: "Office Processing" },
  { value: "submitted_to_nev", label: "Submitted to Nev" },
  { value: "rework_same_so", label: "Reworked same SO" },

  // ✅ NEW: sent to customer
  { value: "sent_to_customer", label: "Sent to customer" },

  { value: "complete", label: "Complete" },
];


const TYPE_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "cyl", label: "Cylinders" },
  { value: "parts", label: "Parts" },
];

const SEARCH_FIELDS = [
  { key: "any", label: "Anything (wide)" },

  // exact-ish identifiers
  { key: "id", label: "Doc ID" },
  { key: "serialNumber", label: "Serial Number (exact)" },
  { key: "returnsNoteNumber", label: "Returns Note Number (exact)" },
  { key: "sretNumber", label: "SRET Number (exact)" },
];


function statusLabel(s) {
  const found = STATUS_OPTIONS.find((x) => x.value === s);
  return found ? found.label : String(s || "new");
}

function statusClasses(status) {
  switch (status) {
    case "new":
      return "bg-blue-50 text-blue-700 border-blue-200";

    case "waiting_collection":
    case "waiting_processing":
      return "bg-amber-50 text-amber-700 border-amber-200";

    case "in_progress":
      return "bg-indigo-50 text-indigo-700 border-indigo-200";

    case "s039_warranty":
      return "bg-violet-50 text-violet-700 border-violet-200";

    case "office_processing":
      return "bg-slate-50 text-slate-700 border-slate-200";

    case "submitted_to_nev":
      return "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200";

    case "sent_to_customer":
      return "bg-teal-50 text-teal-700 border-teal-200";

      case "rework_same_so":
      return "bg-red-50 text-red-700 border-red-200";

    case "complete":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";

    case "straggler":
      return "bg-rose-50 text-rose-700 border-rose-200";

    default:
      return "bg-gray-50 text-gray-700 border-gray-200";
  }
}


function matchScoreRow(row, chips) {
  if (!chips?.length) return 0;

  let score = 0;
  const tokens = Array.isArray(row.searchTokens) ? row.searchTokens : [];
  const hay = tokens.length ? null : buildHaystack(row); // fallback only if no tokens

  for (const chip of chips) {
    const term = chip.term || "";
    if (!term.trim()) continue;

    if (chip.field === "any") {
      const wanted = queryTokens(term);
      if (tokens.length) {
        const hits = wanted.reduce((n, t) => n + (tokens.includes(t) ? 1 : 0), 0);
        if (hits) score += 10 + hits; // any-match: base + per-hit
      } else {
        if (hay?.includes(nrm(term))) score += 10;
      }
      continue;
    }

    // exact-ish fields: if it matches, push it HARD to the top
    if (exactMatchField(row, chip.field, term)) score += 100;
    else score -= 1000; // should already be filtered out, but keeps sort sane if logic changes
  }

  return score;
}


function safeLower(v) {
  return String(v || "").toLowerCase();
}

function stripRnDigits(rn) {
  const s = String(rn || "");
  return s.replace(/\D+/g, "");
}

function getFormFromDoc(d) {
  if (!d) return {};
  if (d.type === "cyl") return d.cyl?.form || {};
  if (d.type === "parts") return d.parts?.form || {};
  return d.cyl?.form || d.parts?.form || {};
}

function getInternalNotesFromDoc(d) {
  const f = getFormFromDoc(d);
  return f.internalNotes || "";
}

function getAdminNotesFromDoc(d) {
  return d.adminNotes || "";
}

function getReturnedPartsFromDoc(d) {
  if (d.type !== "cyl") return [];
  return (
    d.cyl?.returnedParts ||
    d.cyl?.returnedPartsComputed ||
    d.cyl?.returnedPartsList ||
    d.cyl?.returnedParts ||
    []
  );
}

function buildHaystack(d) {
  const f = getFormFromDoc(d);
  const returnedParts = getReturnedPartsFromDoc(d);
  const bits = [
    d.id,
    d.type,
    d.status,
    f.returnsNoteNumber,
    f.serialNumber,
    f.sretNumber,

    // cylinder
    f.cylinderModel,
    f.capacity,
    f.dateOfManufacture,
    f.reasonCode,
    f.reasonDefectCode,
    f.faultFoundCode,
    f.faultDefectCode,

    // parts
    f.partCode,
    f.partTitle,
    f.partDescription,
    f.reportedCode,
    f.faultFoundCode,

    // notes
    f.internalNotes,
    d.adminNotes,
    (returnedParts || []).join(", "),
  ]
    .filter(Boolean)
    .map((x) => String(x));

  return bits.join(" • ").toLowerCase();
}

function nrm(v) {
  return String(v || "").trim().toLowerCase();
}

function digitsOnly(s) {
  return String(s || "").replace(/\D+/g, "");
}

// keep small + ruthless
const STOPWORDS = new Set([
  "a","an","the","and","or","to","of","in","on","for","with","at","by","from","as","is","it","be","was","were",
  "this","that","these","those","are","am","i","you","we","they","he","she","my","your","our","their"
]);

function queryTokens(term) {
  const out = new Set();
  const raw = nrm(term);
  if (!raw) return [];

  // normalize to words (alnum only)
  const phrase = raw.replace(/[^a-z0-9]+/g, " ").trim();
  if (phrase) out.add(phrase);

  // words (drop stopwords + 1-char junk)
  if (phrase) {
    for (const w of phrase.split(/\s+/)) {
      if (!w || w.length < 2) continue;
      if (STOPWORDS.has(w)) continue;
      out.add(w);
    }
  }

  // digits token (useful if user pastes RN-000650 etc)
  const digs = digitsOnly(term);
  if (digs) out.add(digs);

  // compact token (covers your stored model/partCode compaction)
  const compact = raw.replace(/\s+/g, "");
  if (compact) out.add(compact);

  return Array.from(out);
}

function matchesSearchTokens(row, term) {
  const q = nrm(term);
  if (!q) return true;

  // Prefer new search system
  const tokens = Array.isArray(row.searchTokens) ? row.searchTokens : null;
  if (tokens && tokens.length) {
    const wanted = queryTokens(q);
    // "wide" = any match (you said you don't care about false positives)
    return wanted.some((t) => tokens.includes(t));
  }

  // fallback for legacy docs
  return buildHaystack(row).includes(q);
}

function exactMatchField(row, fieldKey, term) {
  const f = getFormFromDoc(row);
  const t = nrm(term);
  if (!t) return true;

  if (fieldKey === "id") {
    return nrm(row.id).includes(t); // keep this flexible
  }

  // exact-ish: compare digits first, then strict normalized string
  const v = f?.[fieldKey];
  const vd = digitsOnly(v);
  const td = digitsOnly(term);

  if (td && vd) return vd === td;
  return nrm(v) === t;
}


function isDateInRange(ts, fromDate, toDate) {
  if (!ts?.toDate) return true;
  const dt = ts.toDate();
  if (fromDate) {
    const from = new Date(fromDate + "T00:00:00");
    if (dt < from) return false;
  }
  if (toDate) {
    const to = new Date(toDate + "T23:59:59");
    if (dt > to) return false;
  }
  return true;
}

/* ----------------- UI helpers (Modal, Chip, etc.) ----------------- */

function Modal({ open, onClose, title, children, widthClass = "max-w-4xl" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-0 flex items-start justify-center p-4 overflow-auto">
        <div className={`w-full ${widthClass} bg-white rounded-xl shadow-2xl border border-gray-200`}>
          <div className="p-4 border-b flex items-center gap-3">
            <div className="text-lg font-semibold text-gray-900">{title}</div>
            <button
              type="button"
              className="ml-auto px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
              onClick={onClose}
            >
              Close
            </button>
          </div>
          <div className="p-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

function Chip({ text, onRemove }) {
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm bg-gray-50">
      {text}
      <button type="button" className="text-red-600" onClick={onRemove} title="Remove">
        ×
      </button>
    </span>
  );
}

function KV({ label, value, mono }) {
  return (
    <div className="flex gap-2">
      <div className="w-52 text-gray-600">{label}</div>
      <div className={mono ? "font-mono" : ""}>{value || "—"}</div>
    </div>
  );
}

function bool(v) {
  return v ? "Yes" : "No";
}

function Section({ title, children }) {
  return (
    <div className="border rounded-xl p-4 bg-white">
      <div className="text-base font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}

function TextArea({ label, value, onChange, readOnly = false }) {
  return (
    <div>
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <textarea
        className={`w-full border rounded px-3 py-2 text-sm ${readOnly ? "bg-gray-100" : "bg-white"}`}
        rows={4}
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        readOnly={readOnly}
      />
    </div>
  );
}

/* ----------------- ReportView / ImagesPanel / Modals ----------------- */

function ReportView({ docData }) {
  const d = docData;
  const f = getFormFromDoc(d);

  if (d.type === "parts") {
    const t = d.parts?.templateSnapshot || {};
    const steps = t.steps || [];
    const test = d.parts?.test || {};

    return (
      <div className="space-y-4">
        <Section title="Return & Part">
          <div className="space-y-1 text-sm">
            <KV label="Returns Note #" value={f.returnsNoteNumber} />
            <KV label="Serial" value={f.serialNumber} mono />
            <KV label="SRET #" value={f.sretNumber} mono />
            <KV label="Part Code" value={f.partCode} />
            <KV label="Part Title" value={f.partTitle || t.title} />
            <KV label="Part Description" value={f.partDescription || t.description} />
          </div>
        </Section>

        <Section title="Reported / Fault Found">
          <div className="space-y-1 text-sm">
            <KV label="Reported" value={f.reportedCode} />
            <KV label="Fault Found" value={f.faultFoundCode} />
          </div>
        </Section>

        {steps.length > 0 && (
          <Section title="Test Procedure">
            <div className="space-y-2 text-sm">
              {steps.map((st) => {
                const v = test[st.id];
                const shown = st.type === "check" ? (v ? "Yes" : "No") : String(v || "—");
                return (
                  <div key={st.id} className="flex gap-3 border rounded px-3 py-2">
                    <div className="w-72 text-gray-700">{st.label}</div>
                    <div className="flex-1">{shown}</div>
                    {st.required ? <span className="text-xs text-rose-600">Required</span> : null}
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {f.internalNotes ? (
          <Section title="Warehouse / Engineer Notes">
            <div className="text-sm whitespace-pre-wrap">{f.internalNotes}</div>
          </Section>
        ) : null}

        {d.adminNotes ? (
          <Section title="Office Notes">
            <div className="text-sm whitespace-pre-wrap text-red-700">{d.adminNotes}</div>
          </Section>
        ) : null}
      </div>
    );
  }

  // cylinder
  const c = d.cyl?.cylCheck || {};
  const leaks = d.cyl?.leaks || {};
  const returnedParts = d.cyl?.returnedParts || [];
  const parts = d.cyl?.parts || {};
  const ops = f.operators || {};


  return (
    <div className="space-y-4">
      <Section title="Return & Cylinder">
        <div className="space-y-1 text-sm">
          <KV label="Returns Note #" value={f.returnsNoteNumber} />
          <KV label="Serial" value={f.serialNumber} mono />
          <KV label="SRET #" value={f.sretNumber} mono />
          <KV label="Model" value={f.cylinderModel} />
          <KV label="Capacity" value={f.capacity} />
          <KV label="Manufacture Date" value={f.dateOfManufacture} />
        </div>
      </Section>
      {(ops.welded || ops.airTest || ops.hotTested || ops.foamTested) ? (
        <Section title="Operators">
          <div className="grid sm:grid-cols-2 gap-2 text-sm">
            <KV label="Welded" value={ops.welded} />
            <KV label="Air tested" value={ops.airTest} />
            <KV label="Hot tested" value={ops.hotTested} />
            <KV label="Foam tested" value={ops.foamTested} />
          </div>
        </Section>
      ) : null}


      <Section title="Reasons & Fault Found">
        <div className="space-y-1 text-sm">
          <KV label="Return Reason" value={f.reasonCode} />
          <KV label="Reason Defect" value={f.reasonDefectCode} />
          <KV label="Fault Found" value={f.faultFoundCode} />
          <KV label="Fault Defect" value={f.faultDefectCode} />
        </div>
      </Section>

      <Section title="Cylinder Checklist">
        <div className="grid sm:grid-cols-2 gap-2 text-sm">
          <KV label="Installed" value={bool(c.cylinderInstalled)} />
          <KV label="Damage: Case" value={bool(c.damageToCase)} />
          <KV label="Damage: Packaging" value={bool(c.damageToPackaging)} />
          <KV label="Damage: Bosses" value={bool(c.damageToBosses)} />
          <KV label="Damage: Pipework/Parts" value={bool(c.damageToPipeworkParts)} />
          <KV label="Damage: Foam" value={bool(c.damageToFoam)} />
          <KV label="Strip & Test" value={bool(c.stripAndTest)} />
        </div>
      </Section>

      <Section title="Leak Checklist">
        <div className="grid sm:grid-cols-2 gap-2 text-sm">
          {Object.keys(leaks || {})
            .filter((k) => k !== "otherComments")
            .map((k) => (
              <KV key={k} label={k} value={bool(leaks[k])} />
            ))}
          {leaks?.other ? <KV label="Other Comments" value={leaks.otherComments} /> : null}
        </div>
      </Section>

      <Section title="Parts Return">
        <div className="space-y-2 text-sm">
          <KV label="No parts returned" value={bool(parts.noPartsReturned)} />
          <KV label="All parts returned" value={bool(parts.allPartsReturned)} />
          <KV label="Partial return" value={bool(parts.partialReturn)} />
          <KV label="System type" value={parts.systemType} />
          <KV label="Returned parts list" value={(returnedParts || []).join(", ")} />
          {parts.notes ? <KV label="Parts notes" value={parts.notes} /> : null}
          {parts.rmaRef ? <KV label="RMA / Ref" value={parts.rmaRef} /> : null}
        </div>
      </Section>

      {f.internalNotes ? (
        <Section title="Warehouse / Engineer Notes">
          <div className="text-sm whitespace-pre-wrap">{f.internalNotes}</div>
        </Section>
      ) : null}

      {d.adminNotes ? (
        <Section title="Office Notes">
          <div className="text-sm whitespace-pre-wrap text-red-700">{d.adminNotes}</div>
        </Section>
      ) : null}
    </div>
  );
}

function ImagesPanelV2({ docId, storage, type }) {
  const [loading, setLoading] = useState(false);
  const [folderUrl, setFolderUrl] = useState(storage?.folderUrl || null);
  const [thumbs, setThumbs] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const folderId = storage?.folderId;
      setFolderUrl(storage?.folderUrl || null);

      if (!folderId) {
        setThumbs([]);
        return;
      }

      setLoading(true);
      try {
        const data = await getThumbsForReturnsV2({ returnsId: docId, storage, type, take: 6 });
        if (cancelled) return;
        setThumbs(data || []);
      } catch (e) {
        console.error(e);
        if (!cancelled) setThumbs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [docId, storage?.folderId, type, storage?.folderUrl]);

  if (!storage?.folderId && !thumbs.length) return null;

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-sm">Photos</div>
        {folderUrl ? (
          <a className="text-xs text-indigo-700 underline" href={folderUrl} target="_blank" rel="noreferrer">
            Open folder
          </a>
        ) : null}
      </div>

      {loading ? <div className="text-xs text-gray-500">Loading images…</div> : null}

      {!loading && thumbs.length === 0 ? (
        <div className="text-xs text-gray-500">No images available.</div>
      ) : null}

      <div className="flex flex-wrap gap-3">
        {thumbs.map((img) => (
          <a
            key={img.id}
            href={img.webUrl}
            target="_blank"
            rel="noreferrer"
            className="w-44 h-44 border rounded overflow-hidden bg-gray-100 block"
            title={img.name}
          >
            <img src={img.thumbUrl || img.webUrl} alt={img.name} className="w-full h-full object-cover" />
          </a>
        ))}
      </div>
    </div>
  );
}

/** Filter modal: builds filter “chips” so you can stack filters. */
function FiltersModal({ open, onClose, filters, setFilters }) {
  const [fromDate, setFromDate] = useState(filters.fromDate || "");
  const [toDate, setToDate] = useState(filters.toDate || "");
  const [type, setType] = useState(filters.type || "all");
  const [submitted, setSubmitted] = useState(filters.submitted || "all");
  const [statusesIncluded, setStatusesIncluded] = useState(filters.statusesIncluded || []);
  const [field, setField] = useState("any");
  const [term, setTerm] = useState("");
  const [chips, setChips] = useState(filters.chips || []);

  useEffect(() => {
    if (!open) return;
    setFromDate(filters.fromDate || "");
    setToDate(filters.toDate || "");
    setType(filters.type || "all");
    setSubmitted(filters.submitted || "all");
    setStatusesIncluded(filters.statusesIncluded || []);
    setChips(filters.chips || []);
    setField("any");
    setTerm("");
  }, [open, filters]);

  const addChip = () => {
    const t = term.trim();
    if (!t) return;
    const label = `${SEARCH_FIELDS.find((x) => x.key === field)?.label || field}: ${t}`;
    setChips((p) => [...p, { id: crypto.randomUUID?.() || String(Date.now()), field, term: t, label }]);
    setTerm("");
  };

  const apply = () => {
    setFilters({ fromDate, toDate, type, submitted, statusesIncluded, chips });
    onClose();
  };

  const clearAll = () => {
    setFromDate("");
    setToDate("");
    setType("all");
    setSubmitted("all");
    setStatusesIncluded([]); // ✅ means "all statuses"
    setChips([]);
    setField("any");
    setTerm("");
  };


  return (
    <Modal open={open} onClose={onClose} title="Filters (pick where to search)">
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <div className="text-xs text-gray-500 mb-1">From date</div>
            <input className="w-full border rounded px-3 py-2 text-sm" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">To date</div>
            <input className="w-full border rounded px-3 py-2 text-sm" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>

          <div>
            <div className="text-xs text-gray-500 mb-1">Type</div>
            <select className="w-full border rounded px-3 py-2 text-sm" value={type} onChange={(e) => setType(e.target.value)}>
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs text-gray-500">Include statuses (empty = all)</div>

              <div className="flex gap-2">
                <button
                  type="button"
                  className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-white"
                  onClick={() => setStatusesIncluded([])}
                >
                  All
                </button>

                <button
                  type="button"
                  className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-white"
                  onClick={() =>
                    setStatusesIncluded(
                      STATUS_OPTIONS
                        .map((s) => s.value)
                        .filter((v) => v !== "complete" && v !== "straggler")
                    )
                  }
                  title="Everything except Complete + Straggler"
                >
                  Active
                </button>

                <button
                  type="button"
                  className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-white"
                  onClick={() => setStatusesIncluded(["waiting_processing", "office_processing", "submitted_to_nev"])}
                  title="Common office workflow"
                >
                  Office flow
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((o) => {
                const active = (statusesIncluded || []).includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() =>
                      setStatusesIncluded((prev) => {
                        const cur = Array.isArray(prev) ? prev : [];
                        if (cur.includes(o.value)) return cur.filter((x) => x !== o.value);
                        return [...cur, o.value];
                      })
                    }
                    className={`px-3 py-2 rounded-full border text-sm transition ${
                      active
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>

            {(statusesIncluded || []).length > 0 ? (
              <div className="mt-2 text-xs text-gray-600">
                Showing ONLY: {statusesIncluded.map(statusLabel).join(", ")}
              </div>
            ) : (
              <div className="mt-2 text-xs text-gray-600">Showing ALL statuses.</div>
            )}
          </div>


        </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">Submitted</div>
            <select
              className="w-full border rounded px-3 py-2 text-sm"
              value={submitted}
              onChange={(e) => setSubmitted(e.target.value)}
            >
              <option value="all">All</option>
              <option value="yes">Submitted only</option>
              <option value="no">Not submitted only</option>
            </select>
          </div>

        <div className="border rounded-xl p-3 bg-gray-50">
          <div className="text-sm font-semibold mb-2">Add a search rule</div>
          <div className="grid sm:grid-cols-[220px_1fr_auto] gap-2 items-end">
            <div>
              <div className="text-xs text-gray-500 mb-1">Search field</div>
              <select className="w-full border rounded px-3 py-2 text-sm" value={field} onChange={(e) => setField(e.target.value)}>
                {SEARCH_FIELDS.map((o) => (
                  <option key={o.key} value={o.key}>{o.label}</option>
                ))}
              </select>
            </div>

            <div>
              <div className="text-xs text-gray-500 mb-1">Search term</div>
              <input
                className="w-full border rounded px-3 py-2 text-sm"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addChip();
                  }
                }}
                placeholder="Type what you want to match…"
              />
            </div>

            <button type="button" className="px-3 py-2 rounded-md border border-gray-300 hover:bg-white" onClick={addChip}>
              Add
            </button>
          </div>

          {chips.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {chips.map((c) => (
                <Chip key={c.id} text={c.label} onRemove={() => setChips((p) => p.filter((x) => x.id !== c.id))} />
              ))}
            </div>
          ) : (
            <div className="mt-2 text-xs text-gray-600">No rules added yet.</div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50" onClick={clearAll}>
            Clear
          </button>
          <button type="button" className="ml-auto px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700" onClick={apply}>
            Apply filters
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DetailModal({ open, onClose, row, onSaveEdits, onGeneratePdf, onOpenInEditor, canEditWarehouse, canEditOffice }) {

  const [editMode, setEditMode] = useState(false);
  const [status, setStatus] = useState(row?.status || "new");
  const [adminNotes, setAdminNotes] = useState(row?.adminNotes || "");
  const [internalNotes, setInternalNotes] = useState(getInternalNotesFromDoc(row));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !row) return;
    setEditMode(false);
    setStatus(row.status || "new");
    setAdminNotes(row.adminNotes || "");
    setInternalNotes(getInternalNotesFromDoc(row));
    setErr("");
    setSaving(false);
  }, [open, row?.id]);

  if (!row) return null;

  const created = row.createdAt?.toDate?.();
  const submitted = row.submittedAt?.toDate?.();
  const typeLabel = row.type === "cyl" ? "Cylinder" : row.type === "parts" ? "Parts" : row.type;
  const allowEdit = canEditWarehouse || canEditOffice;

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const patch = {
        status,
        statusUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (canEditOffice) {
        patch.adminNotes = adminNotes || "";
        patch.notesUpdatedAt = adminNotes?.trim() ? serverTimestamp() : null;
      }

      if (canEditWarehouse) {
        if (row.type === "cyl") patch["cyl.form.internalNotes"] = internalNotes || "";
        if (row.type === "parts") patch["parts.form.internalNotes"] = internalNotes || "";
      }

      await onSaveEdits(row.id, patch);
      setEditMode(false);
    } catch (e) {
      console.error(e);
      setErr("Save failed.");
    } finally {
      setSaving(false);
    }
  };

    const completeReturn = async () => {
    setSaving(true);
    setErr("");
    try {
      const patch = {
        status: "complete",
        statusUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),

        // ✅ your “CompleteAt:” timestamp (field name)
        completedAt: serverTimestamp(),
      };

      await onSaveEdits(row.id, patch);
      setEditMode(false);
    } catch (e) {
      console.error(e);
      setErr("Complete failed.");
    } finally {
      setSaving(false);
    }
  };


  return (
    <Modal open={open} onClose={onClose} title={`Return: ${typeLabel} — ${row.id}`} widthClass="max-w-6xl">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-xs rounded px-2 py-1 border ${statusClasses(row.status)}`}>
            Status: {statusLabel(row.status)}
          </span>
          <span className="text-xs rounded px-2 py-1 border bg-gray-50">Type: {typeLabel}</span>
          <span className="text-xs text-gray-500">Created: {created ? created.toLocaleString() : "—"}</span>
          <span className="text-xs text-gray-500">Submitted: {submitted ? submitted.toLocaleString() : "—"}</span>

          <div className="ml-auto flex gap-2">
            <button type="button" className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50" onClick={onGeneratePdf}>
              PDF
            </button>

            {canEditWarehouse ? (
              <button
                type="button"
                className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
                onClick={onOpenInEditor}
              >
                Open in form
              </button>
            ) : null}


            {allowEdit ? (
              <button
                type="button"
                className={`px-3 py-2 rounded-md border ${
                  editMode ? "border-indigo-600 text-indigo-700" : "border-gray-300"
                } hover:bg-gray-50`}
                onClick={() =>
                  setEditMode((v) => {
                    const next = !v;
                    if (next) {
                      // entering edit mode: sync from row
                      setStatus(row?.status || "new");
                      setAdminNotes(row?.adminNotes || "");
                      setInternalNotes(getInternalNotesFromDoc(row));
                    }
                    return next;
                  })
                }
              >
                {editMode ? "Stop editing" : "Edit"}
              </button>
            ) : null}
          </div>
        </div>

        {editMode ? (
          <Section title="Quick edits">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-gray-500 mb-1">Status</div>
                <select className="w-full border rounded px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)} disabled={saving}>
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {canEditOffice ? (
                <div className="sm:col-span-2">
                  <TextArea label="Office/Admin Notes" value={adminNotes} onChange={setAdminNotes} readOnly={saving} />
                </div>
              ) : null}

              {canEditWarehouse ? (
                <div className="sm:col-span-2">
                  <TextArea label="Warehouse / Engineer Notes" value={internalNotes} onChange={setInternalNotes} readOnly={saving} />
                </div>
              ) : null}
            </div>

            {err ? <div className="text-sm text-red-600 mt-2">{err}</div> : null}

            <div className="mt-3 flex gap-2">
              <button
                type="button"
                className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
                onClick={() => setEditMode(false)}
                disabled={saving}
              >
                Cancel
              </button>

              {/* ✅ NEW: Complete button */}
              <button
                type="button"
                className="px-4 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                onClick={completeReturn}
                disabled={saving}
                title="Marks as complete and stamps completedAt"
              >
                {saving ? "Saving…" : "Complete"}
              </button>

              <button
                type="button"
                className="ml-auto px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                onClick={save}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>

          </Section>
        ) : null}

        <ReportView docData={row} />
        <ImagesPanelV2 docId={row.id} storage={row.storage} type={row.type} />
        <div className="h-2" />
      </div>
    </Modal>
  );
}

/* ===========================
   ✅ Main Viewer (rewritten)
   =========================== */

export default function ReturnsViewerV2({
  canEditWarehouse = true,
  canEditOffice = true,
}) {

  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  const [searchMode, setSearchMode] = useState(false);
  const [searchRows, setSearchRows] = useState([]);
  const [searching, setSearching] = useState(false);

  // pagination: cursor-per-page
  const [pageIndex, setPageIndex] = useState(0);
  const [hasNext, setHasNext] = useState(true);
  const pageLastDocsRef = useRef([]); // last doc snapshot per page index

  const openInEditor = (row) => {
  if (!row?.id) return;
  if (row.type === "cyl") navigate(`/returns/cyl/new?id=${row.id}`);
  if (row.type === "parts") navigate(`/returns/parts/new?id=${row.id}`);
};



  // filters (modal-driven)
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    type: "all",

    // ✅ NEW: include-only list. Empty = "all statuses"
    statusesIncluded: [],

    submitted: "all",
    chips: [],
  });

  const [filtersOpen, setFiltersOpen] = useState(false);


  // detail modal
  const [openRowId, setOpenRowId] = useState(null);

  // ✅ IMPORTANT: when searching, the row lives in searchRows, not rows
  const openRow = useMemo(() => {
    const list = searchMode ? searchRows : rows;
    return list.find((r) => r.id === openRowId) || null;
  }, [rows, searchRows, searchMode, openRowId]);

// inside ReturnsViewerV2 component

  const buildServerQuery = ({ cursor }) => {
    const parts = [];

    // ✅ ALWAYS exclude archived docs server-side
    // IMPORTANT: requires active docs to have archive:false explicitly.
    parts.push(where("archive", "==", false));

    // ✅ server-side filters
    if (filters.type && filters.type !== "all") parts.push(where("type", "==", filters.type));
    // ✅ include-only statuses (Firestore "in" supports up to 10)
    if (Array.isArray(filters.statusesIncluded) && filters.statusesIncluded.length > 0) {
      parts.push(where("status", "in", filters.statusesIncluded));
    }


    // ✅ server-side submitted filter (so page fills with matching docs)
    if (filters.submitted === "yes") {
      parts.push(where("submittedAt", "!=", null));
      parts.push(orderBy("submittedAt", "desc"));
      parts.push(orderBy("createdAt", "desc"));
    } else if (filters.submitted === "no") {
      parts.push(where("submittedAt", "==", null));
      parts.push(orderBy("createdAt", "desc"));
    } else {
      parts.push(orderBy("createdAt", "desc"));
    }

    // ✅ paginate
    if (cursor) parts.push(startAfter(cursor));
    parts.push(limit(PAGE_SIZE));

    return query(collection(db, RETURNS_COL), ...parts);
  };



  async function runAnySearch(term) {
  const wanted = queryTokens(term).slice(0, 10); // cap to avoid spam
  if (!wanted.length) return [];

  const base = [
    where("archive", "==", false),
    ...(filters.type && filters.type !== "all" ? [where("type", "==", filters.type)] : []),
    ...(Array.isArray(filters.statusesIncluded) && filters.statusesIncluded.length
  ? [where("status", "in", filters.statusesIncluded)]
  : []),
  ];

  // Firestore only allows ONE array-contains per query,
  // so you must run multiple queries and union results.
  const qs = wanted.map((tok) =>
    getDocs(
      query(
        collection(db, RETURNS_COL),
        ...base,
        where("searchTokens", "array-contains", tok),
        orderBy("createdAt", "desc"),
        limit(200)
      )
    )
  );

  const snaps = await Promise.all(qs);

  const map = new Map();
  for (const s of snaps) {
    for (const d of s.docs) {
      map.set(d.id, { id: d.id, _snap: d, ...d.data() });
    }
  }

  // optional: tighten results locally (because array-contains is broad)
  const out = Array.from(map.values()).filter((r) => matchesSearchTokens(r, term));
  out.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
  return out;
}


  async function runExactSearch(chips) {
  // pick the first exact chip; you can expand later to AND multiple exacts
  const exactChip = (chips || []).find((c) => c.field && c.field !== "any");
  if (!exactChip) return null;

  const term = exactChip.term || "";
  const field = exactChip.field;

  if (field === "id") {
    const id = term.trim();
    if (!id) return [];
    const snap = await getDoc(doc(db, RETURNS_COL, id));
    if (!snap.exists()) return [];

    const data = snap.data();
    // ✅ if archived, treat as not found in this viewer
    if (data?.archive === true) return [];

    return [{ id: snap.id, _snap: snap, ...data }];
  }


  // Map chip -> Firestore field paths (cyl + parts)
  const fieldPaths =
    field === "serialNumber"
      ? ["cyl.form.serialNumber", "parts.form.serialNumber"]
      : field === "returnsNoteNumber"
      ? ["cyl.form.returnsNoteNumber", "parts.form.returnsNoteNumber"]
      : field === "sretNumber"
      ? ["cyl.form.sretNumber", "parts.form.sretNumber"]
      : [];

  if (!fieldPaths.length) return null;

  // For these exact-ish ones, use your existing digits rule
  // BUT Firestore cannot do “digitsOnly” comparisons. So we use raw equals.
  // If you want digits-equals, store a normalized digits field at write time.
  const value = term.trim();
  if (!value) return [];

  const qs = fieldPaths.map((path) =>
    getDocs(
      query(
        collection(db, RETURNS_COL),

        // ✅ exclude archived
        where("archive", "==", false),

        ...(filters.type && filters.type !== "all" ? [where("type", "==", filters.type)] : []),
        ...(Array.isArray(filters.statusesIncluded) && filters.statusesIncluded.length
        ? [where("status", "in", filters.statusesIncluded)]
        : []),

        where(path, "==", value),
        orderBy("createdAt", "desc"),
        limit(200)
      )
    )
  );


  const snaps = await Promise.all(qs);
  const map = new Map();
  for (const s of snaps) {
    for (const d of s.docs) {
      map.set(d.id, { id: d.id, _snap: d, ...d.data() });
    }
  }

  const out = Array.from(map.values());
  out.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
  return out;
}


  const fetchPage = async (targetIndex) => {
    setLoading(true);
    setError("");

    try {
      let cursor = null;
      if (targetIndex > 0) {
        cursor = pageLastDocsRef.current[targetIndex - 1] || null;
      }

      const q = buildServerQuery({ cursor });
      const snap = await getDocs(q);

      const docs = snap.docs.map((d) => ({
        id: d.id,
        _snap: d,
        ...d.data(),
      }));

      setRows(docs);
      pageLastDocsRef.current[targetIndex] = snap.docs[snap.docs.length - 1] || null;

      setPageIndex(targetIndex);
      setHasNext(snap.size === PAGE_SIZE);
    } catch (e) {
      console.error(e);
      setError("Failed to load returns (Firestore index may be required)");
      setRows([]);
      setHasNext(false);
    } finally {
      setLoading(false);
    }
  };

  const resetAndFetchFirstPage = () => {
    pageLastDocsRef.current = [];
    setPageIndex(0);
    setHasNext(true);
    fetchPage(0);
  };

  const nextPage = () => {
    if (loading) return;
    if (!hasNext) return;
    if (!pageLastDocsRef.current[pageIndex]) return;
    fetchPage(pageIndex + 1);
  };

  const prevPage = () => {
    if (loading) return;
    if (pageIndex <= 0) return;
    fetchPage(pageIndex - 1);
  };

  // initial load
  useEffect(() => {
    resetAndFetchFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    resetAndFetchFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.type, filters.submitted, filters.statusesIncluded]);



useEffect(() => {
  const chips = filters.chips || [];
  const exactChip = chips.find((c) => c.field && c.field !== "any");
  const anyChip = chips.find((c) => c.field === "any" && (c.term || "").trim());

  if (!chips.length) {
    setSearchMode(false);
    setSearchRows([]);
    return;
  }

  (async () => {
    setSearching(true);
    setSearchMode(true);
    setPageIndex(0);
    pageLastDocsRef.current = [];
    setHasNext(false);

    try {
      const res = exactChip
        ? await runExactSearch(chips)
        : anyChip
        ? await runAnySearch(anyChip.term)
        : [];

      setSearchRows(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error(e);
      setSearchRows([]);
    } finally {
      setSearching(false);
    }
  })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [filters.chips, filters.type, filters.statusesIncluded]);



const baseRows = searchMode ? searchRows : rows;

const filtered = useMemo(() => {
  const { fromDate, toDate, chips, submitted } = filters;


  const pass = baseRows.filter((r) => {

    if (submitted === "yes" && !r.submittedAt) return false;
    if (submitted === "no" && !!r.submittedAt) return false;

    if (r.archive === true) return false;


    if (!isDateInRange(r.createdAt, fromDate, toDate)) return false;

    // In searchMode, you already filtered by the exact chip server-side.
    // You can still apply "any" chips locally if you want.
    for (const chip of chips || []) {
      const term = chip.term || "";
      if (!term.trim()) continue;

      if (chip.field === "any") {
        if (!matchesSearchTokens(r, term)) return false;
      } else if (!searchMode) {
        // only do exact filtering locally in browse mode
        if (!exactMatchField(r, chip.field, term)) return false;
      }
    }
    return true;
  });

const statusOrder = Array.isArray(filters.statusesIncluded) ? filters.statusesIncluded : [];

// ✅ If user picked statusesIncluded, group by that order first
const sortByIncludedStatuses = (arr) => {
  if (!statusOrder.length) return arr;
  const idx = (s) => {
    const i = statusOrder.indexOf(s);
    return i === -1 ? 999 : i;
  };
  return [...arr].sort((a, b) => {
    const ai = idx(a.status);
    const bi = idx(b.status);
    if (ai !== bi) return ai - bi;
    return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
  });
};

// Search mode: keep as-is but still apply status grouping if you want
if (searchMode) return sortByIncludedStatuses(pass);

// No chips: just group selected statuses first, then newest
if (!chips?.length) return sortByIncludedStatuses(pass);

// Chips present: keep your relevance scoring, but within same score, apply status grouping
return pass
  .map((r) => ({ r, s: matchScoreRow(r, chips) }))
  .sort((a, b) => {
    if (b.s !== a.s) return b.s - a.s;

    if (statusOrder.length) {
      const ai = statusOrder.indexOf(a.r.status);
      const bi = statusOrder.indexOf(b.r.status);
      const ap = ai === -1 ? 999 : ai;
      const bp = bi === -1 ? 999 : bi;
      if (ap !== bp) return ap - bp;
    }

    return (b.r.createdAt?.toMillis?.() || 0) - (a.r.createdAt?.toMillis?.() || 0);
  })
  .map((x) => x.r);

}, [baseRows, filters, searchMode]);



  const saveEdits = async (id, patch) => {
    const ref = doc(db, RETURNS_COL, id);
    await updateDoc(ref, patch);

    // helper: apply patch to a list (supports dot-path keys)
    const applyPatchToList = (list) =>
      list.map((r) => {
        if (r.id !== id) return r;

        // shallow merge first
        let next = { ...r, ...patch };

        // apply dot-path fields properly
        Object.entries(patch).forEach(([k, v]) => {
          if (!k.includes(".")) return;
          const keys = k.split(".");
          const cloned = { ...next };
          let cur = cloned;
          for (let i = 0; i < keys.length - 1; i++) {
            cur[keys[i]] = { ...(cur[keys[i]] || {}) };
            cur = cur[keys[i]];
          }
          cur[keys[keys.length - 1]] = v;
          next = cloned;
        });

        return next;
      });

    // ✅ optimistic update BOTH lists (browse + search)
    setRows((prev) => applyPatchToList(prev));
    setSearchRows((prev) => applyPatchToList(prev));

    // ✅ refresh doc from Firestore and update BOTH lists
    try {
      const snap = await getDoc(doc(db, RETURNS_COL, id));
      if (snap.exists()) {
        const fresh = { id, _snap: snap, ...snap.data() };
        setRows((prev) => prev.map((r) => (r.id === id ? fresh : r)));
        setSearchRows((prev) => prev.map((r) => (r.id === id ? fresh : r)));
      }
    } catch (e) {
      console.warn("Refresh after save failed", e);
    }
  };


  const generatePdf = async (row) => {
    try {
      const spec = await buildReturnsV2PdfSpec(row);
      await pdfDownloadFromSpec(spec);
    } catch (e) {
      console.error(e);
      alert(e?.message || "PDF generation failed.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="text-2xl font-bold">Returns Viewer (V2)</div>

        <div className="sm:ml-auto flex flex-wrap gap-2">
          <button
            type="button"
            className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
            onClick={() => setFiltersOpen(true)}
          >
            Filters
          </button>

          <button
            type="button"
            className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
            onClick={resetAndFetchFirstPage}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Active filter summary */}
      <div className="mt-3 flex flex-wrap gap-2 items-center">
        <span className="text-sm text-gray-600">
          Showing <b>{filtered.length}</b> of {rows.length} loaded (page size {PAGE_SIZE})
        </span>

        {filters.type !== "all" ? (
          <Chip text={`Type: ${filters.type}`} onRemove={() => setFilters((p) => ({ ...p, type: "all" }))} />
        ) : null}
        {(filters.statusesIncluded || []).length > 0 ? (
          <Chip
            text={`Statuses: ${(filters.statusesIncluded || []).map(statusLabel).join(", ")}`}
            onRemove={() => setFilters((p) => ({ ...p, statusesIncluded: [] }))}
          />
        ) : null}
        {filters.fromDate ? (
          <Chip text={`From: ${filters.fromDate}`} onRemove={() => setFilters((p) => ({ ...p, fromDate: "" }))} />
        ) : null}
        {filters.toDate ? (
          <Chip text={`To: ${filters.toDate}`} onRemove={() => setFilters((p) => ({ ...p, toDate: "" }))} />
        ) : null}
        {(filters.chips || []).map((c) => (
          <Chip
            key={c.id}
            text={c.label}
            onRemove={() => setFilters((p) => ({ ...p, chips: (p.chips || []).filter((x) => x.id !== c.id) }))}
          />
        ))}
      </div>

      {error ? <div className="mt-3 text-sm text-red-600">{error}</div> : null}

      {/* List */}
      <div className="mt-4 space-y-3">
        {loading && rows.length === 0 ? <div className="text-gray-500">Loading…</div> : null}
        {!loading && filtered.length === 0 ? <div className="text-gray-500">No results (change filters).</div> : null}
        {searching ? <div className="text-gray-500">Searching…</div> : null}


        {filtered.map((r) => {
          const f = getFormFromDoc(r);
          const dt = r.createdAt?.toDate?.();
          const title =
            r.type === "cyl"
              ? f.cylinderModel || "—"
              : r.type === "parts"
              ? f.partCode || f.partTitle || "—"
              : "—";

          return (
            <div key={r.id} className="border rounded-xl bg-white">
              <div className="p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs rounded px-2 py-1 border bg-gray-50">
                      {r.type === "cyl" ? "Cylinder" : r.type === "parts" ? "Parts" : r.type}
                    </span>
                    <span className="font-medium truncate max-w-xs sm:max-w-md">{title}</span>
                    <span className={`text-xs rounded px-2 py-1 border ${statusClasses(r.status)}`}>
                      {statusLabel(r.status)}
                    </span>
                    {r.submittedAt ? (
                      <span className="text-xs rounded px-2 py-1 border bg-emerald-50 text-emerald-700 border-emerald-200">
                        Submitted
                      </span>
                    ) : (
                      <span className="text-xs rounded px-2 py-1 border bg-gray-50 text-gray-700">
                        Not submitted
                      </span>
                    )}
                  </div>

                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-600">
                    <span>
                      RN: <span className="font-mono">{f.returnsNoteNumber || "—"}</span>
                    </span>
                    <span>
                      Serial: <span className="font-mono">{f.serialNumber || "—"}</span>
                    </span>
                    <span>
                      SRET: <span className="font-mono">{f.sretNumber || "—"}</span>
                    </span>
                    <span className="text-gray-400">ID: {r.id.slice(-8)}</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2">
                  <div className="text-xs sm:text-sm text-gray-500">{dt ? dt.toLocaleString() : "—"}</div>

                  <button
                    type="button"
                    className="text-indigo-700 hover:underline text-xs sm:text-sm"
                    onClick={() => setOpenRowId(r.id)}
                  >
                    View
                  </button>

                  <button
                    type="button"
                    className="px-2 py-1 rounded-md border border-gray-300 hover:bg-gray-50 text-xs sm:text-sm"
                    onClick={() => generatePdf(r)}
                  >
                    PDF
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center gap-2">
        <button
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
          disabled={loading || pageIndex === 0}
          onClick={prevPage}
        >
          Prev
        </button>
        <span className="text-sm text-gray-600">Page {pageIndex + 1}</span>
        <button
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
          disabled={loading || !hasNext}
          onClick={nextPage}
        >
          Next
        </button>
      </div>

      <FiltersModal
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={filters}
        setFilters={setFilters}
      />

      <DetailModal
        open={!!openRowId}
        onClose={() => setOpenRowId(null)}
        row={openRow}
        onSaveEdits={saveEdits}
        onGeneratePdf={() => openRow && generatePdf(openRow)}
        onOpenInEditor={() => openRow && openInEditor(openRow)}
        canEditWarehouse={canEditWarehouse}
        canEditOffice={canEditOffice}
      />
    </div>
  );
}
